import { goSolidApi } from "@/composables/useGoSolidApi";
import { isAxiosError } from "axios";
import DOMPurify from "dompurify";
import { marked } from "marked";

/**
 * Country issuance ops notes. The hand-edited sources live in the
 * soldera-org/country-operations-claw repo (countries/<name>/processes/
 * issuance.md); its CI derives a render-ready artifact that the backend
 * serves at /api/admin/issuance/docs, so doc edits go live without a gosolid
 * deploy. Maintained by Patryk — countries without a file fall back to a
 * "contact Patryk" placeholder.
 *
 * Each doc carries frontmatter with deterministic facts (registry, cadence,
 * deadlines, …) for agent consumers, followed by a human narrative. The panel
 * renders selected frontmatter fields as a fact card and the body as markdown.
 */

export interface IssuanceDocFact {
  label: string;
  value: string;
  href?: string;
}

export interface IssuanceDoc {
  facts: IssuanceDocFact[];
  html: string;
}

interface IssuanceDocResponse {
  country: string;
  frontmatter: Record<string, unknown>;
  body_markdown: string;
}

/**
 * Country codes that have an issuance doc; drives the grid's doc icons.
 * GET /api/admin/issuance/docs → { countries: string[] }.
 *
 * @throws {AxiosError} on any backend or network failure — the caller decides
 * whether missing icons are worth surfacing.
 */
export const fetchIssuanceDocCountries = async (): Promise<string[]> => {
  const response = await goSolidApi.get<{ countries: string[] }>(
    "/api/admin/issuance/docs"
  );
  return response.data.countries;
};

/**
 * The country's doc as fact-card entries (from the frontmatter) plus rendered
 * narrative HTML. GET /api/admin/issuance/docs/<country> →
 * { country, frontmatter, body_markdown }.
 *
 * Returns null only for a 404 (the country genuinely has no doc — show the
 * placeholder). Any other failure is a real error, not the no-doc state.
 *
 * @throws {AxiosError} on backend or network failures other than 404
 * SECURITY: the HTML is only ever fed into v-html because the sources are
 * Soldera-authored repo files served by our own backend, not user input.
 */
export const issuanceDoc = async (
  country: string
): Promise<IssuanceDoc | null> => {
  let doc: IssuanceDocResponse;
  try {
    const response = await goSolidApi.get<IssuanceDocResponse>(
      `/api/admin/issuance/docs/${country}`
    );
    doc = response.data;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) return null;
    throw error;
  }

  return {
    facts: extractFacts(doc.frontmatter),
    // SECURITY: marked passes raw inline HTML through, and write access to the
    // docs repo/bucket is broader than gosolid admin — sanitize so a doc can
    // never run script in an admin session.
    html: DOMPurify.sanitize(
      marked.parse(doc.body_markdown, { async: false, gfm: true })
    ),
  };
};

// Frontmatter fields shown on the fact card, in display order. Everything else
// (nested deadlines, statuses, …) stays agent-only in the source file.
const FACT_FIELDS: ReadonlyArray<[key: string, label: string]> = [
  ["registry", "Registry"],
  ["issuing_body", "Issuing body"],
  ["issuance_cadence", "Cadence"],
  ["issuance_method", "Method"],
  ["production_basis", "Production basis"],
  ["portal", "Portal"],
  ["last_verified", "Last verified"],
];

const extractFacts = (
  frontmatter: Record<string, unknown>
): IssuanceDocFact[] => {
  const facts: IssuanceDocFact[] = [];
  for (const [key, label] of FACT_FIELDS) {
    const raw = frontmatter[key];
    const value = formatFactValue(raw);
    if (!value) continue;
    // Link only single-URL values — a joined array of URLs is not a valid href.
    const href =
      typeof raw === "string" && /^https?:\/\//.test(raw) ? raw : undefined;
    facts.push({
      label,
      value: href
        ? value.replace(/^https?:\/\//, "")
        : value.includes("://")
          ? value
          : prettify(value),
      href,
    });
  }
  return facts;
};

const formatFactValue = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  if (Array.isArray(value)) {
    const items = value.map(formatFactValue).filter(Boolean);
    return items.length ? items.join(", ") : null;
  }
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  // Nested objects (deadlines, regional cadences) don't fit a one-line fact.
  return null;
};

// snake_case enum-ish values read better as plain words on the card.
const prettify = (value: string): string => value.replace(/_/g, " ");
